import React, { useState } from 'react';
import { useIdentity } from './IdentityContext';
import { useConfirm } from './ConfirmContext';
import { useToast } from './ToastContext';
import Spinner from './Spinner';
import { GOLD, INK } from '../theme';

const NOUNS = {
  collections: { singular: 'collection', plural: 'collections' },
  categories: { singular: 'category', plural: 'categories' },
};

const count = (n, singular, plural) => `${n} ${n === 1 ? singular : plural}`;

// Diffs draft vs. published lists by slug so the publish confirmation can
// say exactly what's about to change instead of a blind "publish everything".
function diffLists(draftList, publishedList, keyField = 'slug') {
  let added = 0;
  let modified = 0;
  let unchanged = 0;
  for (const item of draftList) {
    const match = publishedList.find((p) => p[keyField] === item[keyField]);
    if (!match) added++;
    else if (JSON.stringify(item) === JSON.stringify(match)) unchanged++;
    else modified++;
  }
  const draftKeys = new Set(draftList.map((i) => i[keyField]));
  const removed = publishedList.filter((p) => !draftKeys.has(p[keyField])).length;
  return { added, modified, unchanged, removed };
}

export default function PublishBar({ endpoint = '/.netlify/functions/collections-publish', label = 'Publish changes' }) {
  const { authFetch } = useIdentity();
  const confirm = useConfirm();
  const toast = useToast();
  const [publishing, setPublishing] = useState(false);
  const [checking, setChecking] = useState(false);

  const listKey = endpoint.includes('categories') ? 'categories' : 'collections';
  const noun = NOUNS[listKey];
  const draftEndpoint = endpoint.replace('-publish', '-draft');
  const publicEndpoint = endpoint.replace('-publish', '-public');

  const handlePublish = async () => {
    setChecking(true);
    let summary;
    try {
      const [draftRes, publicRes] = await Promise.all([authFetch(draftEndpoint), fetch(publicEndpoint)]);
      const draftDoc = draftRes.ok ? await draftRes.json() : null;
      const publicDoc = publicRes.ok ? await publicRes.json() : null;
      if (!draftDoc) throw new Error('Failed to load draft state');
      const draftList = draftDoc[listKey] || [];
      const publishedList = publicDoc ? publicDoc[listKey] || [] : [];
      summary = diffLists(draftList, publishedList);
    } catch (err) {
      toast.error(err.message || 'Could not check draft status. Try again.');
      setChecking(false);
      return;
    }
    setChecking(false);

    const { added, modified, unchanged, removed } = summary;
    if (added + modified + removed === 0) {
      toast.error('Nothing to publish — the live site already matches your drafts.');
      return;
    }

    const changeParts = [];
    if (added) changeParts.push(count(added, `new ${noun.singular}`, `new ${noun.plural}`));
    if (modified) changeParts.push(count(modified, 'update', 'updates'));
    if (removed) changeParts.push(count(removed, 'removal', 'removals'));

    let message = `This will publish: ${changeParts.join(', ')}.`;
    if (unchanged) message += `\n${count(unchanged, `already-published ${noun.singular}`, `already-published ${noun.plural}`)} will stay unchanged.`;
    if (removed) message += `\n\nWarning: ${count(removed, `live ${noun.singular}`, `live ${noun.plural}`)} no longer in the draft will be removed from the site.`;

    const ok = await confirm(message, { danger: removed > 0, confirmLabel: 'Publish' });
    if (!ok) return;

    setPublishing(true);
    try {
      const res = await authFetch(endpoint, { method: 'POST' });
      const data = await res.json();
      if (!res.ok) {
        const details = data.problems && data.problems.length ? ` ${data.problems.join(' ')}` : '';
        throw new Error((data.error || 'Publish failed') + details);
      }
      toast.success(`Published at ${new Date(data.publishedAt).toLocaleTimeString()}.`);
    } catch (err) {
      toast.error(err.message || 'Publish failed.');
    } finally {
      setPublishing(false);
    }
  };

  const busy = publishing || checking;

  return (
    <div style={{ position: 'sticky', bottom: 0, display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap', padding: '16px 20px', background: 'rgba(22,17,12,.92)', border: '1px solid rgba(201,162,75,.35)', backdropFilter: 'blur(6px)' }}>
      <button
        onClick={handlePublish}
        disabled={busy}
        style={{ display: 'flex', alignItems: 'center', gap: 10, fontWeight: 600, fontSize: 13, letterSpacing: '.2em', textTransform: 'uppercase', padding: '14px 32px', background: GOLD, color: INK, border: 'none', cursor: busy ? 'default' : 'pointer', opacity: busy ? .6 : 1 }}
      >
        {busy && <Spinner size={14} />}
        {publishing ? 'Publishing…' : checking ? 'Checking…' : label}
      </button>
    </div>
  );
}
